/**
 * Shared field validation — keep in sync with server/src/validations/fields.js
 */

export const LIMITS = {
  text: 100,
  textarea: 500,
  phone: 10,
  otp: 8,
  passwordMin: 6,
  passwordMax: 100,
};

export const EMAIL_RE = /^[a-z0-9]+(?:\.[a-z0-9]+)*@[a-z0-9]+(?:\.[a-z0-9]+)+$/;
export const PHONE_RE = /^\d{10}$/;
export const NUMBER_RE = /^\d+(\.\d+)?$/;

export function isText(value, { required = false, max = LIMITS.text, label = 'Field' } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? `${label} is required` : null;
  if (v.length > max) return `${label} cannot exceed ${max} characters`;
  return null;
}

export function isTextarea(value, { required = false, max = LIMITS.textarea, label = 'Message' } = {}) {
  return isText(value, { required, max, label });
}

export function isNumber(value, { required = false, label = 'Number' } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? `${label} is required` : null;
  if (!NUMBER_RE.test(v)) return `${label}: only numbers are allowed`;
  return null;
}

export function isEmail(value, { required = true } = {}) {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return required ? 'Email is required' : null;
  if (/\s/.test(String(value))) return 'Email cannot contain spaces';
  if (!EMAIL_RE.test(v)) {
    return 'Use lowercase email with letters/numbers only; dots between characters (e.g. name@domain.com)';
  }
  return null;
}

export function isPhone(value, { required = true } = {}) {
  const v = String(value ?? '').replace(/\D/g, '');
  if (!v) return required ? 'Phone number is required' : null;
  if (!PHONE_RE.test(v)) return 'Phone must be exactly 10 digits';
  return null;
}

export function isPassword(value, { required = true } = {}) {
  const v = String(value ?? '');
  if (!v) return required ? 'Password is required' : null;
  if (v.length < LIMITS.passwordMin) return `Password must be at least ${LIMITS.passwordMin} characters`;
  if (v.length > LIMITS.passwordMax) return `Password maximum ${LIMITS.passwordMax} characters`;
  return null;
}

export function normalizeEmail(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9.@]/g, '')
    .slice(0, LIMITS.text);
}

export function normalizePhone(value) {
  return String(value ?? '').replace(/\D/g, '').slice(0, LIMITS.phone);
}

export function normalizeNumber(value) {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) return cleaned.slice(0, LIMITS.text);
  return `${parts[0]}.${parts.slice(1).join('')}`.slice(0, LIMITS.text);
}

export function normalizeOtp(value) {
  return String(value ?? '').replace(/\D/g, '').slice(0, LIMITS.otp);
}

/** Login identifier — email if it contains @ or letters, otherwise phone digits */
export function normalizeIdentifier(value) {
  const raw = String(value ?? '');
  if (raw.includes('@') || /[a-zA-Z]/.test(raw)) {
    return normalizeEmail(raw);
  }
  return normalizePhone(raw);
}

export function isIdentifier(value, { required = true } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? 'Email or phone is required' : null;
  if (v.includes('@') || /[a-zA-Z]/.test(v)) return isEmail(v);
  return isPhone(v);
}

export function clampText(value, max = LIMITS.text) {
  return String(value ?? '').slice(0, max);
}

export function clampTextarea(value, max = LIMITS.textarea) {
  return String(value ?? '').slice(0, max);
}

/** Live-filter value by field kind */
export function sanitizeByKind(kind, value) {
  switch (kind) {
    case 'email':
      return normalizeEmail(value);
    case 'phone':
      return normalizePhone(value);
    case 'number':
      return normalizeNumber(value);
    case 'otp':
      return normalizeOtp(value);
    case 'identifier':
      return normalizeIdentifier(value);
    case 'textarea':
      return clampTextarea(value);
    case 'password':
      return String(value ?? '').slice(0, LIMITS.passwordMax);
    case 'text':
    default:
      return clampText(value);
  }
}

export function validateByKind(kind, value, opts = {}) {
  switch (kind) {
    case 'email':
      return isEmail(value, opts);
    case 'phone':
      return isPhone(value, opts);
    case 'number':
      return isNumber(value, opts);
    case 'textarea':
      return isTextarea(value, opts);
    case 'password':
      return isPassword(value, opts);
    case 'otp':
      return isNumber(value, { ...opts, label: 'OTP' }) ||
        (String(value).length < 4 ? 'OTP must be at least 4 digits' : null);
    case 'identifier':
      return isIdentifier(value, opts);
    case 'text':
    default:
      return isText(value, opts);
  }
}
